import { CACHE_TIERS } from "@workspace/types";
import { useCurrentUserDisplay } from "@/hooks/shared/use-auth";
import { trpc } from "@/lib/trpc/react";

interface UseCommunityShowReviewsInfiniteOptions {
	limit?: number;
	excludeCurrentUser?: boolean;
}

/**
 * Simple community show reviews hook - directly returns the tRPC query result
 * Matches mobile implementation for consistency
 */
export function useCommunityShowReviewsInfinite(
	showId: string,
	options: UseCommunityShowReviewsInfiniteOptions = {},
) {
	const { limit = 10, excludeCurrentUser = false } = options;
	const { data: currentUser } = useCurrentUserDisplay();

	const excludeUserId = excludeCurrentUser && currentUser?.id ? currentUser.id : undefined;

	const result = trpc.show.getCommunityShowReviews.useQuery(
		{
			showId,
			limit,
			offset: 0, // For now, just show first 10 reviews (can add pagination later)
			excludeUserId,
		},
		{
			enabled: !!showId && showId.length > 0,
			...CACHE_TIERS.DYNAMIC, // Community reviews
		},
	);

	// Transform to match the expected infinite query format
	// This maintains compatibility with the component
	return {
		data: result.data ? { pages: [result.data], pageParams: [0] } : undefined,
		fetchNextPage: () => {}, // No-op for now
		hasNextPage: false, // Simple implementation for now
		isFetchingNextPage: false,
		isLoading: result.isLoading,
		error: result.error,
		refetch: result.refetch,
	};
}
